import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ProductForm.css';
import '../../../shared/StyledInput.css';
import ControlFlags from '../../../shared/ControlFlags';
import FieldHelp from '../../../shared/FieldHelp';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import DateInput from '../../../shared/DateInput';
import { ProductThumb } from '../../../shared/ProductThumb';
import DuplicateBanner from '../../../shared/DuplicateBanner';
import ProductQuickBooksSection from './ProductQuickBooksSection';
import brandLabel from '../../../shared/brandLabel';

function ProductForm({ initial, categories: categoriesProp, suppliers: suppliersProp, uoms: uomsProp, productTypes: productTypesProp, brands: brandsProp, onSave, onCancel, loading, onManageCategories, onManageSuppliers, onManageUoms, onManageProductTypes, onManageBrands, onQuickBooksChange }) {
  const [sku, setSku] = useState(initial?.sku || '');
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [purchase_description, setPurchaseDescription] = useState(initial?.purchase_description || '');
  const [supplier_id, setSupplierId] = useState(initial?.supplier_id ?? '');
  const [supplier_sku, setSupplierSku] = useState(initial?.supplier_sku || '');
  const [product_category_id, setProductCategoryId] = useState(initial?.product_category_id ?? '');
  const [brand_id, setBrandId] = useState(initial?.brand_id ?? '');
  const [product_price_group_id, setProductPriceGroupId] = useState(initial?.product_price_group_id ?? '');
  const [base_uom_id, setBaseUomId] = useState(initial?.base_uom_id ?? '');
  const [uom_control_type_id, setUomControlTypeId] = useState('');
  const [fetchedUomControlTypes, setFetchedUomControlTypes] = useState([]);
  const [product_type_id, setProductTypeId] = useState(initial?.product_type_id ?? '');
  const [availability_date, setAvailabilityDate] = useState(initial?.availability_date || '');
  const [availability_override, setAvailabilityOverride] = useState(initial?.availability_override || '');
  const [hide_pre_availability_badge, setHidePreAvailabilityBadge] = useState(!!initial?.hide_pre_availability_badge);
  const [hide_new_badge, setHideNewBadge] = useState(!!initial?.hide_new_badge);
  const [error, setError] = useState('');
  const [fetchedCategories, setFetchedCategories] = useState([]);
  const [fetchedBrands, setFetchedBrands] = useState([]);
  const [fetchedProductPriceGroups, setFetchedProductPriceGroups] = useState([]);
  const [fetchedSuppliers, setFetchedSuppliers] = useState([]);
  const [fetchedUoms, setFetchedUoms] = useState([]);
  const [fetchedProductTypes, setFetchedProductTypes] = useState([]);
  const [defaultBaseUomId, setDefaultBaseUomId] = useState('');
  const [defaultProductTypeId, setDefaultProductTypeId] = useState('');
  const [defaultUomControlTypeId, setDefaultUomControlTypeId] = useState('');
  const [primaryImage, setPrimaryImage] = useState(null);

  // Read-only preview of the Marketing-primary image (or a placeholder) —
  // a saved product only, so editing/uploading the image itself still
  // happens on Product Media (kebab → Product Media), not this form.
  useEffect(() => {
    if (!initial?.id) return setPrimaryImage(null);
    axios.get('/api/media-images/primary-for-products', { params: { product_ids: initial.id } })
      .then((res) => setPrimaryImage(res.data.data?.[String(initial.id)] || null))
      .catch(() => setPrimaryImage(null));
  }, [initial?.id]);

  useEffect(() => {
    if (categoriesProp) return;
    axios.get('/api/product-categories')
      .then((res) => setFetchedCategories(res.data.data || []))
      .catch(() => setFetchedCategories([]));
  }, [categoriesProp]);

  useEffect(() => {
    if (brandsProp) return;
    axios.get('/api/brands', { params: { status: 'active' } })
      .then((res) => setFetchedBrands(res.data.data || []))
      .catch(() => setFetchedBrands([]));
  }, [brandsProp]);

  useEffect(() => {
    axios.get('/api/product-price-groups')
      .then((res) => setFetchedProductPriceGroups(res.data.data || []))
      .catch(() => setFetchedProductPriceGroups([]));
  }, []);

  useEffect(() => {
    if (suppliersProp) return;
    axios.get('/api/supplier-entities', { params: { status: 'active' } })
      .then((res) => setFetchedSuppliers(res.data.data || []))
      .catch(() => setFetchedSuppliers([]));
  }, [suppliersProp]);

  useEffect(() => {
    if (uomsProp) return;
    axios.get('/api/uoms', { params: { status: 'active' } })
      .then((res) => setFetchedUoms(res.data.data || []))
      .catch(() => setFetchedUoms([]));
  }, [uomsProp]);

  // Only needed for a brand-new product's initial Base UoM row — an
  // existing SAVED product's UoM Control Type assignments are managed on
  // the dedicated UoM page, same split as Base UoM itself above. Keyed off
  // initial?.id rather than initial itself: Duplicate seeds `initial` from
  // a source product but with id stripped, so it needs this same fresh
  // Base UoM row as any other new product — matching validate()'s and the
  // JSX's own initial?.id check just below.
  useEffect(() => {
    if (initial?.id) return;
    axios.get('/api/uom-control-types', { params: { status: 'active' } })
      .then((res) => setFetchedUomControlTypes(res.data.data || []))
      .catch(() => setFetchedUomControlTypes([]));
  }, [initial]);

  // Prefers Inventory Settings' explicit Default UoM Control Type. Falls back
  // to the first Can Track-capable seeded type (by code order) only when no
  // default has been configured yet — the same accidental behavior this
  // setting replaces, kept as a safety net rather than a hard requirement.
  useEffect(() => {
    if (initial?.id || uom_control_type_id !== '' || fetchedUomControlTypes.length === 0) return;
    const configuredDefault = defaultUomControlTypeId
      ? fetchedUomControlTypes.find((t) => String(t.id) === String(defaultUomControlTypeId))
      : null;
    const defaultType = configuredDefault
      || fetchedUomControlTypes.find((t) => t.is_system_default && t.can_track)
      || fetchedUomControlTypes.find((t) => t.can_track);
    if (defaultType) setUomControlTypeId(defaultType.id);
    // eslint-disable-next-line
  }, [fetchedUomControlTypes, defaultUomControlTypeId]);

  useEffect(() => {
    if (productTypesProp) return;
    axios.get('/api/product-types', { params: { status: 'active' } })
      .then((res) => setFetchedProductTypes(res.data.data || []))
      .catch(() => setFetchedProductTypes([]));
  }, [productTypesProp]);

  useEffect(() => {
    if (initial) return;
    axios.get('/api/inventory-settings')
      .then((res) => {
        setDefaultBaseUomId(res.data.data?.default_base_uom_id ?? '');
        setDefaultProductTypeId(res.data.data?.default_product_type_id ?? '');
        setDefaultUomControlTypeId(res.data.data?.default_uom_control_type_id ?? '');
      })
      .catch(() => {
        setDefaultBaseUomId('');
        setDefaultProductTypeId('');
        setDefaultUomControlTypeId('');
      });
    // eslint-disable-next-line
  }, []);

  const categories = categoriesProp || fetchedCategories;
  const categoryOptions = categories.filter(
    (c) => c.is_active || String(c.id) === String(product_category_id)
  );

  const brands = brandsProp || fetchedBrands;
  const brandOptions = brands.filter(
    (b) => b.is_active || String(b.id) === String(brand_id)
  );

  const productPriceGroupOptions = fetchedProductPriceGroups.filter(
    (g) => g.is_active || String(g.id) === String(product_price_group_id)
  );

  const suppliers = suppliersProp || fetchedSuppliers;
  const supplierOptions = suppliers.filter(
    (s) => s.is_active || String(s.id) === String(supplier_id)
  );

  const uoms = uomsProp || fetchedUoms;
  const baseUomOptions = uoms.filter(
    (u) => u.is_active || String(u.id) === String(base_uom_id)
  );

  const productTypes = productTypesProp || fetchedProductTypes;
  const productTypeOptions = productTypes.filter(
    (s) => s.is_active || String(s.id) === String(product_type_id)
  );

  // Cost Price is its own dated schedule now (issue #139), managed from the
  // Cost Price screen (kebab menu), not this form — once any schedule entry
  // exists, the Supplier link is fixed (mixing currency contexts on one
  // schedule isn't allowed), so the Supplier field locks here too rather
  // than only failing on save.
  const supplierLocked = !!initial?.has_cost_price_history;

  useEffect(() => {
    if (initial || product_category_id !== '' || categories.length === 0) return;
    const defaultCategory = categories.find((c) => c.is_system_default) || categories[0];
    if (defaultCategory) setProductCategoryId(defaultCategory.id);
    // eslint-disable-next-line
  }, [categories]);

  useEffect(() => {
    if (initial || base_uom_id !== '' || !defaultBaseUomId) return;
    if (uoms.some((u) => String(u.id) === String(defaultBaseUomId))) {
      setBaseUomId(defaultBaseUomId);
    }
    // eslint-disable-next-line
  }, [defaultBaseUomId, uoms]);

  useEffect(() => {
    if (initial || product_type_id !== '' || !defaultProductTypeId) return;
    if (productTypes.some((s) => String(s.id) === String(defaultProductTypeId))) {
      setProductTypeId(defaultProductTypeId);
    }
    // eslint-disable-next-line
  }, [defaultProductTypeId, productTypes]);

  const validate = () => {
    if (!sku.trim()) return 'SKU is required.';
    if (!name.trim()) return 'Name is required.';
    if (!initial?.id && !base_uom_id) return 'Base Unit of Measure is required.';
    if (!initial?.id && !uom_control_type_id) return 'UoM Control Type is required.';
    if (!product_type_id) return 'Product Type is required.';
    if (!brand_id) return 'Brand is required.';

    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      sku: sku.trim(),
      name: name.trim(),
      description: description.trim(),
      purchase_description: purchase_description.trim(),
      supplier_id: supplier_id === '' ? '' : Number(supplier_id),
      supplier_sku: supplier_sku.trim(),
      product_category_id: product_category_id === '' ? '' : Number(product_category_id),
      brand_id: brand_id === '' ? '' : Number(brand_id),
      product_price_group_id: product_price_group_id === '' ? '' : Number(product_price_group_id),
      product_type_id: Number(product_type_id),
      availability_date: availability_date || '',
      availability_override: availability_override || '',
      hide_pre_availability_badge,
      hide_new_badge,
      // Weight/dimensions removed from Product entirely (issue #100) — the
      // old flat kg/cm fields were too limited a concept (no per-UoM
      // packaging, no rate-based weight); rebuilt as its own concept there.
      // Base UoM is only ever submitted at creation — an existing product's
      // Base UoM (and every other UoM row) is managed on the dedicated UoM
      // page from here on (issue #95).
      ...(initial?.id ? {} : {
        base_uom_id: Number(base_uom_id),
        uoms: [{ uom_id: Number(base_uom_id), conversion_rate: 1, uom_control_type_id: Number(uom_control_type_id) }],
      }),
    });
  };

  const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
  const sectionStyle = { marginTop: 16, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };

  return (
    <form className="product-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      {initial?._duplicateSourceId && <DuplicateBanner label="Product" />}
      <div style={{ ...sectionStyle, marginTop: 0 }}>
        <h4 style={sectionHeaderStyle}>Identity</h4>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end' }}>
          {initial?.id && (
            <div title="Marketing-primary image — managed on Product Media (kebab → Product Media)">
              <ProductThumb entry={primaryImage} size={147} variant="preview" />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 14 }}>
              <div>
                <label>SKU<RequiredMark /></label><br />
                <input
                  className="styled-input"
                  value={sku}
                  onChange={(e) => setSku(e.target.value.slice(0, 50))}
                  maxLength={50}
                  placeholder="e.g., WID-001"
                  style={{ width: 280 }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label>Name<RequiredMark /></label><br />
                <input
                  className="styled-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  placeholder="e.g., Widget"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <label>Description</label><br />
              <textarea
            className="styled-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
            placeholder="Optional notes"
          />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
      <div style={{ ...sectionStyle, marginTop: 0, flex: 1 }}>
        <h4 style={sectionHeaderStyle}>Classification</h4>
        <div>
          <label>Product Type<RequiredMark /></label>{' '}
          <FieldHelp fieldKey="products.product-type" pageKey="products" articleSlug="product-types" articleTitle="Product Types">
            Classifies what kind of product this is (Purchased, Manufactured, Kit, Consumable, ...) and sets the ceiling for which Inventory Controls (Buy/Sell/Pick) its Units of Measure are allowed to have.
          </FieldHelp>
          <br />
          <select
            className="styled-input"
            value={product_type_id}
            onChange={(e) => setProductTypeId(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">(select)</option>
            {productTypeOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
            ))}
          </select>
          {onManageProductTypes && (
            <button
              type="button"
              onClick={onManageProductTypes}
              style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
            >
              Manage product types
            </button>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <label>Category</label><br />
          <select
            className="styled-input"
            value={product_category_id}
            onChange={(e) => setProductCategoryId(e.target.value)}
            style={{ width: 220 }}
          >
            {categoryOptions.map((c) => (
              <option key={c.id} value={c.id}>{'\u00A0\u00A0\u00A0'.repeat(c.depth || 0)}{c.name}</option>
            ))}
          </select>
          {onManageCategories && (
            <button
              type="button"
              onClick={onManageCategories}
              style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
            >
              Manage categories
            </button>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <label>Brand<RequiredMark /></label><br />
          <select
            className="styled-input"
            value={brand_id}
            onChange={(e) => setBrandId(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">(select)</option>
            {brandOptions.map((b) => (
              <option key={b.id} value={b.id}>{brandLabel(b)}</option>
            ))}
          </select>
          {onManageBrands && (
            <button
              type="button"
              onClick={onManageBrands}
              style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
            >
              Manage brands
            </button>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <label>Product Price Group</label><br />
          <select
            className="styled-input"
            value={product_price_group_id}
            onChange={(e) => setProductPriceGroupId(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">(none)</option>
            {productPriceGroupOptions.map((g) => (
              <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ ...sectionStyle, marginTop: 0, flex: 1 }}>
        <h4 style={sectionHeaderStyle}>Availability</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label title="Drives the Hidden / Backorder / Available lifecycle and the Coming Soon / New badges. Leave blank for an always-available SKU.">Availability Date</label><br />
            <DateInput
              value={availability_date}
              onChange={(e) => setAvailabilityDate(e.target.value)}
              style={{ width: 180 }}
            />
          </div>
          <div>
            <label title="Forces the lifecycle state regardless of Availability Date. Leave as System default to let the date (and Platform Settings' lead time) decide. 'Hidden' is the only setting that removes this SKU from the Pricelist entirely.">Override</label><br />
            <select
              className="styled-input"
              value={availability_override}
              onChange={(e) => setAvailabilityOverride(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">System default</option>
              <option value="hidden">Hidden</option>
              <option value="backorder">Backorder</option>
              <option value="preorder">Pre-order</option>
              <option value="available">Available</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} title="Suppresses the 'Available <date>' / 'Pre-order' / 'Backorder' badge on this SKU without affecting its lifecycle state.">
            <input
              type="checkbox"
              checked={hide_pre_availability_badge}
              onChange={(e) => setHidePreAvailabilityBadge(e.target.checked)}
            />
            Hide pre-availability badge
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} title="Suppresses the 'New' badge once the Availability Date has passed.">
            <input
              type="checkbox"
              checked={hide_new_badge}
              onChange={(e) => setHideNewBadge(e.target.checked)}
            />
            Hide "New" badge
          </label>
        </div>
      </div>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Supplier Information</h4>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <label title={supplierLocked ? 'This Product already has Cost Price schedule history, so its Supplier link is fixed — remove the schedule entries first if this is a genuine correction.' : undefined}>Supplier</label><br />
            <select
              className="styled-input"
              value={supplier_id}
              onChange={(e) => setSupplierId(e.target.value)}
              disabled={supplierLocked}
              title={supplierLocked ? 'This Product already has Cost Price schedule history, so its Supplier link is fixed.' : undefined}
              style={{ width: 220 }}
            >
              <option value="">(none)</option>
              {supplierOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {onManageSuppliers && (
              <button
                type="button"
                onClick={onManageSuppliers}
                style={{ marginLeft: 10, background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}
              >
                Manage suppliers
              </button>
            )}
          </div>

          <div>
            <label>Supplier SKU</label><br />
            <input
              className="styled-input"
              value={supplier_sku}
              onChange={(e) => setSupplierSku(e.target.value)}
              maxLength={100}
              placeholder="Supplier's own SKU/part number"
              style={{ width: 180 }}
            />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <label title="Shown on purchase order lines instead of the product name, if different">Purchase Description</label><br />
          <input
            className="styled-input"
            value={purchase_description}
            onChange={(e) => setPurchaseDescription(e.target.value)}
            maxLength={100}
            placeholder="Description shown on purchase orders, if different"
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
          Cost Price is managed on its own dated schedule — see this product's kebab menu → Cost Price{initial?.id ? '' : ' (once saved)'}.
        </div>
      </div>

      {initial?.id && (
        <ProductQuickBooksSection product={initial} sectionStyle={sectionStyle} sectionHeaderStyle={sectionHeaderStyle} onChange={onQuickBooksChange} />
      )}

      {!initial?.id && (
      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Units of Measure</h4>
          <div>
            <label>Base Unit of Measure<RequiredMark /></label><br />
            <select
              className="styled-input"
              value={base_uom_id}
              onChange={(e) => setBaseUomId(e.target.value)}
              style={{ width: 200 }}
            >
              <option value="">(select)</option>
              {baseUomOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
              ))}
            </select>

            <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
              Additional Units of Measure, conversion rates, and pricing are managed from the Product list once this product is saved.
            </div>

            {onManageUoms && (
              <div style={{ marginTop: 8, textAlign: 'left' }}>
                <button
                  type="button"
                  onClick={onManageUoms}
                  style={{ display: 'block', background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, marginLeft: 0 }}
                >
                  Manage units of measure
                </button>
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <label title="Sets this row's Can Buy/Sell/Pick/Track — must have Can Track on, since the Base UoM is always Tracked.">UoM Control Type<RequiredMark /></label>{' '}
              <FieldHelp fieldKey="products.uom-control-type" pageKey="products" articleSlug="uom-control-types-locked" articleTitle="UoM Control Types — Locked">
                Each Control Type sets a fixed Buy/Sell/Pick/Track combination — pick the one matching how this Product's Base UoM is actually bought, sold, picked, and tracked.
              </FieldHelp>
              <br />
              <select
                className="styled-input"
                value={uom_control_type_id}
                onChange={(e) => setUomControlTypeId(e.target.value)}
                style={{ width: 200 }}
              >
                <option value="">(select)</option>
                {fetchedUomControlTypes.filter((t) => t.can_track).map((t) => (
                  <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
                ))}
              </select>
              {uom_control_type_id && (
                <div style={{ marginTop: 4 }}>
                  <ControlFlags type={fetchedUomControlTypes.find((t) => String(t.id) === String(uom_control_type_id))} />
                </div>
              )}
            </div>
          </div>
      </div>
      )}

      {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}

      <div style={{ marginTop: 16 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default ProductForm;
